import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import s from './ModelCategory.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { initialModelCategoryState } from '../../reducers/initialState';
import { v4 as uuidv4 } from 'uuid';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Banner from '../Banner/Banner';
import { Navigate, Link, useParams } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import classNames from 'classnames';
import Cookies from 'js-cookie';
import Checkbox from '../WSAControls/CheckboxContainer/Checkbox';
import Dropdown from '../WSAControls/DroprdownContainer/Dropdown';
import OperationalSetupContentTemplate from '../OperationalSetup/OperationalSetupContentTemplate';
import { compareValues } from '../../utils/';

const ModelCategory = ({
    saveModelCategory,
    describeModelCategory,
    modelCategoryManager = {
        ...initialModelCategoryState.modelCategoryManager
    },
    userProfileWidgets,
    changeModelCategory,
    modelCategoryCleanUp,
    intl
}) => {
    let IsEdit = false;
    let IsRedirect = false;
    let heading = '';
    const [localformErrors, setFormErrors] = useState({});
    const envName = process.env.REACT_APP_ENV_NAME_SHORT;
    const organisationId = Cookies.get(`selectedorganisationid-${envName}`) || 0;
    let { ModelCategoryId } = useParams();
    let widgetCode = 'MODELCATEGORY_ADD';
    if (ModelCategoryId) {
        IsEdit = true;
        widgetCode = 'MODELCATEGORY_UPDATE';
        heading = intl.formatMessage({ id: 'modelCategory.edit', defaultMessage: 'Edit Model Category' });
    } else {
        ModelCategoryId = '0';
        IsEdit = false;
        heading = intl.formatMessage({ id: 'modelCategory.add', defaultMessage: 'Add Model Category' });
    }

    useEffect(() => {
        if (ModelCategoryId) {
            describeModelCategory(ModelCategoryId, organisationId, widgetCode);
        }
    }, [describeModelCategory, ModelCategoryId, organisationId, widgetCode]);

    useEffect(() => {
        return () => {
            modelCategoryCleanUp({
                ...initialModelCategoryState.modelCategoryManager
            });
        };
    }, []);

    // Update redux store
    const setLocalModelCategory = currentState => {
        changeModelCategory(currentState);
    };

    const onDropdownCategoryTypeChange = e => {

        if (!e || !e.value) {
            return;
        }
        setLocalModelCategory({
            selectedModelCategory: { ...modelCategoryManager.selectedModelCategory, ModelCategoryTypeId: e.value }
        });

    };

    const categoryTypeOptions = () => {
        let catTypeOptions = [];

        modelCategoryManager.modelCategoryTypes.forEach(element => {
            catTypeOptions.push({
                label: element.Name,
                value: element.ModelCategoryTypeId
            });
        });
        return catTypeOptions.sort(compareValues('label'));
    };

    //Saving
    const submitForm = e => {
        e.preventDefault();
        if (!validateInput()) {
            return;
        }
        let saveData = modelCategoryManager.selectedModelCategory;
        saveData.IsEdit = IsEdit;
        saveData.UpdatedBy = Cookies.get(`userid-${envName}`) || 0;
        saveData.OwnerId = Cookies.get(`selectedorganisationid-${envName}`) || 1;
        if (!IsEdit) {
            saveData.ModelCategoryId = 0;
            saveData.GuidIdentifier = uuidv4();
        }
        //saving ModelCategory
        saveModelCategory(saveData, widgetCode);
    };

    const validateInput = () => {
        let formErrors = {};
        let isValid = true;


        if (
            !modelCategoryManager ||
            !modelCategoryManager.selectedModelCategory ||
            !modelCategoryManager.selectedModelCategory.ModelCategoryTypeId
        ) {
            formErrors.ModelCategoryTypeId = intl.formatMessage({
                id: 'modelCategory.modelCategoryTypeMandatory',
                defaultMessage: 'Model category type is a mandatory field'
            });
            isValid = false;
        }

        if (!modelCategoryManager || !modelCategoryManager.selectedModelCategory || !modelCategoryManager.selectedModelCategory.Name) {
            formErrors.Name = intl.formatMessage({
                id: 'modelCategory.nameMandatory',
                defaultMessage: 'Model category name is a mandatory field'
            });
            isValid = false;
        }
        setFormErrors(formErrors);
        return isValid;
    };

    //on control value change
    const onChange = e => {
        setLocalModelCategory({
            ...modelCategoryManager,
            selectedModelCategory: { ...modelCategoryManager.selectedModelCategory, [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }
        });
    };

    let messageId = (modelCategoryManager && modelCategoryManager.displayMessageCode) || 'none';
    const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });

    return (
        <div className={s.modelCategory}>
            {modelCategoryManager.isLoading && <LoadingSpinner />}
            {modelCategoryManager.isOpSuccessful && !modelCategoryManager.showBanner ? <Navigate to="/operational-setup/model-category-list" /> : ''}
            {IsRedirect ? <Navigate to="/operational-setup/model-category-list" /> : ''}
            <Banner
                key={uuidv4()}
                failureText={messageText}
                showBanner={modelCategoryManager.showBanner}
                status={modelCategoryManager.isOpSuccessful}
                successText={messageText}
            />
            <OperationalSetupContentTemplate selectedPage="manageModelCategory" userProfileWidgets={userProfileWidgets}>
                <div className={s.contentWrapper}>
                    <div className={s.modelCategoryHeader}>
                        <Link to="/operational-setup/model-category-list" className={s.backLink}>
                            &lt; &nbsp;
                            <FormattedMessage id="modelCategory.backToList" defaultMessage="BACK TO MODEL CATEGORIES" />
                        </Link>
                        <h4>{heading}</h4>
                    </div>
                    <div className={s.modelCategoryContent}>
                        <Form>
                            <Row>
                                <Col lg={4}>
                                    <Form.Group controlId="formModelCategoryType">
                                        <Form.Label>
                                            <FormattedMessage id="modelCategory.modelCategoryType" defaultMessage="Model Category Type" />
                                        </Form.Label>
                                        <Dropdown
                                            id="ddlCategoryType"
                                            dataArray={categoryTypeOptions()}
                                            disabled={ModelCategoryId && parseInt(ModelCategoryId) > 0 ? true : false}
                                            controlData={{
                                                placeholderText: (
                                                    <FormattedMessage id="common.selectOne" defaultMessage="Please select one" />
                                                )
                                            }}
                                            onDropdownChange={onDropdownCategoryTypeChange}
                                            selectedOption={categoryTypeOptions().filter(
                                                option =>
                                                    option.value === modelCategoryManager.selectedModelCategory.ModelCategoryTypeId
                                            )}
                                        />
                                        {localformErrors && localformErrors.ModelCategoryTypeId && (
                                            <p role="alert" className={s.error}>
                                                {localformErrors.ModelCategoryTypeId}
                                            </p>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col lg={6}>
                                    <Form.Group controlId="formModelCategoryName">
                                        <Form.Label>
                                            <FormattedMessage id="modelCategory.name" defaultMessage="Model Category Name" />
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="Name"
                                            onChange={onChange}
                                            value={modelCategoryManager.selectedModelCategory.Name}
                                            className={`${s.formControl} ${localformErrors && localformErrors.Name ? s.formControlError : ''}`}
                                        />
                                        {localformErrors && localformErrors.Name && (
                                            <p role="alert" className={s.error}>
                                                {localformErrors.Name}
                                            </p>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col lg={6}>
                                    <Form.Group controlId="formModelCategoryDescription">
                                        <Form.Label>
                                            <FormattedMessage id="modelCategory.description" defaultMessage="Description" />
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows="3"
                                            name="Description"
                                            onChange={onChange}
                                            placeholder={intl.formatMessage({
                                                id: 'modelCategory.enterDescription',
                                                defaultMessage: 'Enter description'
                                            })}
                                            value={modelCategoryManager.selectedModelCategory.Description}
                                            className={s.textArea}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col lg={6}>
                                    <Form.Group controlId="formTransferAllowed">
                                        <Checkbox
                                            key={uuidv4()}
                                            dataArray={[
                                                {
                                                    SKEY: 'TransferAllowed',
                                                    target: { type: 'checkbox' },
                                                    label: intl.formatMessage({
                                                        id: 'modelCategory.transferAllowed',
                                                        defaultMessage: 'Transfer Allowed'
                                                    }),
                                                    isChecked: modelCategoryManager.selectedModelCategory.TransferAllowed
                                                }
                                            ]}
                                            onSelectionChange={onChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col lg={6}>

                                    <Form.Group controlId="formDuplicateAllowed">
                                        <Checkbox
                                            key={uuidv4()}
                                            dataArray={[
                                                {
                                                    SKEY: 'ShareAllowed',
                                                    target: { type: 'checkbox' },
                                                    label: intl.formatMessage({
                                                        id: 'modelCategory.shareAllowed',
                                                        defaultMessage: 'Share Allowed'
                                                    }),
                                                    isChecked: modelCategoryManager.selectedModelCategory.ShareAllowed
                                                }
                                            ]}
                                            onSelectionChange={onChange}
                                        />
                                        <Checkbox
                                            key={uuidv4()}
                                            dataArray={[
                                                {
                                                    SKEY: 'DuplicateAllowed',
                                                    target: { type: 'checkbox' },
                                                    label: intl.formatMessage({
                                                        id: 'modelCategory.duplicateAllowed',
                                                        defaultMessage: 'Duplicate Allowed'
                                                    }),
                                                    isChecked: modelCategoryManager.selectedModelCategory.DuplicateAllowed
                                                }
                                            ]}
                                            onSelectionChange={onChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col lg={6}>
                                    <Form.Group controlId="formIsActive">
                                        <Checkbox
                                            key={uuidv4()}
                                            dataArray={[
                                                {
                                                    SKEY: 'IsActive',
                                                    target: { type: 'checkbox' },
                                                    label: intl.formatMessage({
                                                        id: 'modelCategory.isActive',
                                                        defaultMessage: 'Is Active'
                                                    }),
                                                    isChecked: modelCategoryManager.selectedModelCategory.IsActive
                                                }
                                            ]}
                                            onSelectionChange={onChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Button
                                        variant="primary"
                                        className={classNames(s.margin5, s.btnSaveChanges)}
                                        onClick={submitForm}
                                        noValidate
                                        data-unittest="saveData"
                                    >
                                        <FormattedMessage id="modelCategory.save" defaultMessage="SAVE CHANGES" />
                                    </Button>
                                    <Link to="/operational-setup/model-category-list">
                                        <Button variant="outline-secondary" className={s.btnCancel}>
                                            <FormattedMessage id="modelCategory.cancel" defaultMessage="CANCEL" />
                                        </Button>
                                    </Link>
                                </Col>
                            </Row>
                        </Form>
                    </div>
                </div>
            </OperationalSetupContentTemplate>
        </div >
    );
};

ModelCategory.propTypes = {
    saveModelCategory: PropTypes.func.isRequired,
    modelCategoryManager: PropTypes.object.isRequired,
    describeModelCategory: PropTypes.func.isRequired,
    changeModelCategory: PropTypes.func.isRequired,
    modelCategoryCleanUp: PropTypes.func.isRequired
};

export default injectIntl(ModelCategory);
